import { Link } from 'react-router-dom';
import type { Listing } from '../types';
import AspectRatioImage from './AspectRatioImage';
import SmartImage from './SmartImage';

function makeSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-');
}

function getListingUrl(listing: Listing): string {
  // Use category_slug if available, otherwise fall back to category_id
  const catSlug = listing.category_slug || listing.category_id;
  const listingSlug = listing.slug || makeSlug(listing.title);
  return `/listing/${catSlug}/${listingSlug}`;
}

interface Props {
  listing: Listing;
  /** Larger featured variant: fixed 16:9 cover image + bigger type. */
  large?: boolean;
}

export default function ListingCard({ listing, large = false }: Props) {
  const badgeClass = listing.badge === 'featured'
    ? 'bg-accent text-bg'
    : listing.badge === 'premium'
    ? 'bg-amber text-bg'
    : '';
  const badgeText = listing.badge === 'featured' ? 'Featured'
    : listing.badge === 'premium' ? 'Premium'
    : '';
  const fallbackImg = 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=600&q=80';
  const img = listing.image_url || fallbackImg;

  return (
    <Link to={getListingUrl(listing)} className="listing-card group">
      {large ? (
        <div
          className="relative overflow-hidden bg-elevated-light dark:bg-elevated listing-card-image"
          style={{ aspectRatio: '16 / 9' }}
        >
          <SmartImage
            src={img}
            alt={listing.title}
            fallback={fallbackImg}
            mode="cover"
            className="absolute inset-0 w-full h-full transition-transform duration-400 group-hover:scale-105"
          />
          {badgeText && (
            <span className={`absolute top-3 left-3 px-3 py-0.5 rounded-full text-[0.7rem] font-semibold uppercase tracking-wide ${badgeClass}`}>
              {badgeText}
            </span>
          )}
        </div>
      ) : (
        <AspectRatioImage
          src={img}
          alt={listing.title}
          fallback={fallbackImg}
          frameClassName="listing-card-image"
          className="transition-transform duration-400 group-hover:scale-105"
        >
          {badgeText && (
            <span className={`absolute top-3 left-3 px-3 py-0.5 rounded-full text-[0.7rem] font-semibold uppercase tracking-wide ${badgeClass}`}>
              {badgeText}
            </span>
          )}
        </AspectRatioImage>
      )}
      <div className={large ? 'p-5 sm:p-6' : 'p-4 sm:p-5'}>
        <div className="text-xs text-accent font-semibold uppercase tracking-wide mb-2">
          {listing.category_name}
        </div>
        <div className={`font-semibold ${large ? 'text-[1.1rem] sm:text-[1.25rem]' : 'text-[1rem] sm:text-[1.05rem]'} mb-2 text-text-primary-light dark:text-text-primary`}>
          {listing.title}
        </div>
        <div className="flex items-center gap-1 text-xs sm:text-sm text-text-secondary mb-3">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {listing.region_name}, NZ
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)]">
          <div className="flex items-center gap-1 text-xs sm:text-sm font-medium">
            {listing.rating > 0 && (
              <>
                <span className="text-amber">★</span> {listing.rating}
                <span className="text-text-muted">({listing.reviews_count})</span>
              </>
            )}
            {listing.rating === 0 && <span className="text-text-muted text-xs">No reviews yet</span>}
          </div>
          <div className="text-[0.7rem] sm:text-xs text-text-muted">View details →</div>
        </div>
      </div>
    </Link>
  );
}
